import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import type { DataflowSummary } from "@shared/types/dataflows";
import { invalidateConnectionLists } from "@/hooks/use-overview";
import { useStartRun } from "@/hooks/use-runs";
import { ApiClientError, newIdempotencyKey } from "@/lib/api";
import { startedRuns } from "@/lib/live/InstanceRunFeed";

/**
 * "Import selected" on the connections list (Session 11, P1; rulings D2 and D1).
 *
 * N ordinary starts, not a bulk route: each selected Ready, idle connection gets its own
 * `POST /runs` with its own Idempotency-Key, one at a time in the grid's order, so each writes its
 * own `run.start` audit row through the existing registry entry. A skipped row sends nothing. A
 * refusal for one connection (the busy 409, 403, 422, 429, a 5xx or a network failure) is recorded
 * against that row with the server's own sentence and the loop goes on to the next.
 *
 * Every start is marked in the tab's started-runs registry as quiet, so its row reads Queued and
 * then follows the instance socket without a reload, while the list's live line keeps speaking
 * for a single-row Import now only. The connections list is invalidated once, at the end.
 */

/**
 * At most this many connections per click, the first in the grid's order. The rest are not
 * attempted: they come back as `overLimit`, stay selected on the page and are named in the bar,
 * so a second click starts them (contract §2.2: the result says why a row did not start).
 */
export const IMPORT_SELECTED_MAX = 50;

export const SKIP_DRAFT = "Setup incomplete: validate it and select a table first.";
export const SKIP_DISABLED = "Disabled: enable it to import.";
/** A state this build does not know (see `stateSkipReason`); not reachable with today's enum. */
const SKIP_NOT_READY = "Only a Ready connection can be imported.";
/**
 * A skipped row of Import selected, read after the click beside the row's name. Its own sentence,
 * not the off control's tooltip (`IMPORT_OFF_WHILE_RUNNING`, #1081), which says when it turns on.
 */
export const SKIP_IMPORTING = "An import is already running.";
export const REFUSED_FALLBACK = "The import could not be started. Try this one again.";

export interface ImportSelectedRow {
  dataflowId: string;
  name: string;
}

export interface NotStartedRow extends ImportSelectedRow {
  reason: string;
}

export interface StartedRow extends ImportSelectedRow {
  runId: string;
}

export interface ImportSelectedOutcome {
  started: StartedRow[];
  /** Skipped and refused rows, in the order they were selected in the grid. */
  notStarted: NotStartedRow[];
  /** Rows past `IMPORT_SELECTED_MAX`, in the grid's order: not attempted on this click. */
  overLimit: ImportSelectedRow[];
}

/** The bar's line for the rows past the cap (they stay selected). */
export function overLimitLine(count: number): string {
  return count === 1
    ? `1 more is still selected (at most ${IMPORT_SELECTED_MAX} per click): press Import selected again.`
    : `${count} more are still selected (at most ${IMPORT_SELECTED_MAX} per click): press Import selected again.`;
}

function nameOf(row: DataflowSummary): string {
  return row.displayName;
}

/**
 * Why a connection in this state is never started, or null for Ready. Exhaustive: a new state
 * added to `DataflowSummary["state"]` fails the type check here until it is given its own reason
 * (review r2), rather than silently reading the Draft sentence.
 */
function stateSkipReason(state: DataflowSummary["state"]): string | null {
  switch (state) {
    case "ready":
      return null;
    case "draft":
      return SKIP_DRAFT;
    case "disabled":
      return SKIP_DISABLED;
    default: {
      const unhandled: never = state;
      void unhandled;
      // Only reachable if the server sends a state this build does not know: never start it.
      return SKIP_NOT_READY;
    }
  }
}

/**
 * Which rows start and which are skipped, with the predicates the row menu uses: only a Ready
 * connection with no import queued or running (as this tab knows it) is started.
 */
export function planImportSelected(
  rows: readonly DataflowSummary[],
  isImporting: (row: DataflowSummary) => boolean,
): { toStart: DataflowSummary[]; skipped: NotStartedRow[] } {
  const toStart: DataflowSummary[] = [];
  const skipped: NotStartedRow[] = [];
  for (const row of rows) {
    const base = { dataflowId: row.id, name: nameOf(row) };
    const stateReason = stateSkipReason(row.state);
    if (stateReason !== null) skipped.push({ ...base, reason: stateReason });
    else if (isImporting(row)) skipped.push({ ...base, reason: SKIP_IMPORTING });
    else toStart.push(row);
  }
  return { toStart, skipped };
}

/** The server's own sentence for a refusal, else the generic one (never an empty line). */
export function refusalReason(error: unknown): string {
  if (error instanceof ApiClientError && error.message.trim().length > 0) return error.message;
  return REFUSED_FALLBACK;
}

export interface ImportSelectedProgress {
  /** 1-based: the start in flight. */
  index: number;
  total: number;
}

export function useImportSelected(): {
  importSelected: (
    rows: readonly DataflowSummary[],
    isImporting: (row: DataflowSummary) => boolean,
  ) => Promise<ImportSelectedOutcome>;
  progress: ImportSelectedProgress | null;
} {
  const { mutateAsync } = useStartRun();
  const queryClient = useQueryClient();
  const [progress, setProgress] = useState<ImportSelectedProgress | null>(null);
  // A second click while a batch runs must not start a second batch.
  const running = useRef(false);

  const importSelected = useCallback(
    async (
      rows: readonly DataflowSummary[],
      isImporting: (row: DataflowSummary) => boolean,
    ): Promise<ImportSelectedOutcome> => {
      if (running.current) return { started: [], notStarted: [], overLimit: [] };
      running.current = true;
      const order = new Map(rows.map((row, index) => [row.id, index]));
      const { toStart, skipped } = planImportSelected(
        rows.slice(0, IMPORT_SELECTED_MAX),
        isImporting,
      );
      const overLimit = rows
        .slice(IMPORT_SELECTED_MAX)
        .map((row) => ({ dataflowId: row.id, name: nameOf(row) }));
      const started: StartedRow[] = [];
      const refused: NotStartedRow[] = [];
      try {
        for (const [index, row] of toStart.entries()) {
          setProgress({ index: index + 1, total: toStart.length });
          try {
            const result = await mutateAsync({
              body: { dataflowId: row.id },
              idempotencyKey: newIdempotencyKey(),
            });
            startedRuns.mark(
              result.runId,
              result.dataflowIds && result.dataflowIds.length > 0 ? result.dataflowIds : [row.id],
              { quiet: true },
            );
            started.push({ dataflowId: row.id, name: nameOf(row), runId: result.runId });
          } catch (error) {
            refused.push({ dataflowId: row.id, name: nameOf(row), reason: refusalReason(error) });
          }
        }
      } finally {
        running.current = false;
        setProgress(null);
        if (started.length > 0) {
          // #2094 review: the Overview's own copy of the list too, not only the Connections list.
          invalidateConnectionLists(queryClient);
        }
      }
      const notStarted = [...skipped, ...refused].sort(
        (a, b) => (order.get(a.dataflowId) ?? 0) - (order.get(b.dataflowId) ?? 0),
      );
      return { started, notStarted, overLimit };
    },
    [mutateAsync, queryClient],
  );

  return { importSelected, progress };
}
